import React from 'react';
import { Card, Col, Row, Form } from 'react-bootstrap';
import PrefilledFieldShell from './PrefilledFieldShell';
import { resolveFieldMode } from '../requestDraftFieldPolicy';
import { scalarEqualsSnapshot } from '../draftFormUtils';

/** @typedef {{ key: string, label: string, type?: string }} ExpField */

/** @type {ExpField[]} */
const EXPERIENCE_FIELDS = [
  { key: 'internshipTraining', label: 'Internship training' },
  { key: 'postGraduationInternship', label: 'Post‑graduation internship' },
  { key: 'internshipCompletion', label: 'Internship completion' },
  { key: 'internshipStart', label: 'Internship start', type: 'date' },
  { key: 'internshipEnd', label: 'Internship end', type: 'date' },
  { key: 'additionalExperience', label: 'Additional experience' },
  { key: 'undergraduateInternship', label: 'Undergraduate internship' },
];

function EduReadonlyCard({ row, idx }) {
  return (
    <Card className="mb-2 border-secondary-subtle">
      <Card.Body className="py-2">
        <div className="small text-muted mb-1">Education {idx + 1}</div>
        <div>
          <strong>{row.degreeName || '—'}</strong>
          {' · '}
          {row.institutionName || '—'}
        </div>
        <div className="small text-muted">
          {[row.fieldOfStudy, row.countryOfCertificate, row.graduationDate]
            .filter(Boolean)
            .join(' · ')}
        </div>
        {row.accreditationDocument?.originalName ? (
          <div className="small mt-1">
            Doc: {row.accreditationDocument.originalName} (#
            {row.accreditationDocument.id})
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

export default function QualificationsSection({
  education,
  experience,
  baselineExperience,
  policy,
  onExperienceChange,
  forceReadOnly = false,
}) {
  const eduMode = forceReadOnly
    ? 'readOnly'
    : resolveFieldMode('qualifications.education', policy);
  const eduReadOnly = eduMode === 'readOnly';

  const expCert = experience?.internshipCertificate;
  const expCertSummary = expCert
    ? `${expCert.originalName || 'Certificate'} (#${expCert.id})`
    : '—';

  return (
    <>
      <Card className="mb-3 shadow-sm">
        <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
          <span>Education</span>
          {eduReadOnly ? (
            <span className="small text-muted fw-normal">
              prefilled snapshot
            </span>
          ) : null}
        </Card.Header>
        <Card.Body>
          {!education?.length ? (
            <p className="text-muted mb-0">No education rows on this draft.</p>
          ) : (
            education.map((row, idx) =>
              eduReadOnly ? (
                <EduReadonlyCard key={`edu-${idx}`} row={row} idx={idx} />
              ) : (
                <Form.Group key={`edu-edit-${idx}`} className="mb-3">
                  <Form.Label className="fw-semibold">{`Education ${idx + 1}`}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={JSON.stringify(row, null, 2)}
                    readOnly
                    className="font-monospace small"
                  />
                </Form.Group>
              )
            )
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3 shadow-sm">
        <Card.Header className="fw-semibold">Experience</Card.Header>
        <Card.Body>
          <Row>
            {EXPERIENCE_FIELDS.map(({ key, label, type }) => {
              const path = `qualifications.experience.${key}`;
              const mode = forceReadOnly
                ? 'readOnly'
                : resolveFieldMode(path, policy);
              const cur = experience?.[key] ?? '';
              const base = baselineExperience?.[key];
              const highlight =
                mode === 'editable' &&
                scalarEqualsSnapshot(cur, base) &&
                String(cur).length > 0;
              return (
                <Col md={6} key={key}>
                  <PrefilledFieldShell
                    label={label}
                    htmlId={`req-draft-exp-${key}`}
                    value={cur == null ? '' : String(cur)}
                    type={type || 'text'}
                    mode={mode}
                    highlightPrefilled={highlight}
                    readOnlyReason={mode === 'readOnly' ? 'policy' : undefined}
                    onChange={
                      mode === 'editable'
                        ? (v) => onExperienceChange(key, v)
                        : undefined
                    }
                  />
                </Col>
              );
            })}
          </Row>
          <div className="small text-muted border-top pt-3 mt-1">
            <span className="fw-semibold text-body">
              Internship certificate (snapshot):{' '}
            </span>
            {expCertSummary}
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
